import { escapeAttr } from "./linkPreview";

/**
 * Which Skypoint environment the app switcher opens its peer apps in, set at runtime (issue #864).
 *
 * It used to be compiled into the bundle (`VITE_SKY_ENVIRONMENT`), which no build ever set, so
 * every deployed image opened production's apps and one image digest could not open each
 * environment's own. Now the web writes its own `SKY_ENVIRONMENT` into the document it serves, as
 * one `<meta>` tag, the same way it writes the browser's App Insights connection string
 * (`shared/browserTelemetry.ts`, #851): the tag is in the document before any script runs, needs
 * no request and is not inline script. The client reads it in `client/src/lib/env.ts`; no tag
 * (unset, a local run) keeps the old default, production for a production bundle and development
 * otherwise.
 *
 * Public by nature: it only chooses which of the switcher's published addresses to open.
 */

/** The values the app switcher accepts (`SkyEnvironment` in `@skypointcloud/skypoint-ui`). */
export const SKY_ENVIRONMENTS = ["development", "qa", "production"] as const;

export type SkyEnvironmentName = (typeof SKY_ENVIRONMENTS)[number];

/** The `name` of the tag. */
export const SKY_ENVIRONMENT_META = "skydata-sky-environment";

/** True for one of `SKY_ENVIRONMENTS`. */
export function isSkyEnvironment(value: unknown): value is SkyEnvironmentName {
  return typeof value === "string" && (SKY_ENVIRONMENTS as readonly string[]).includes(value);
}

/** The document with the tag added before `</head>`, or unchanged when no environment is set. */
export function injectSkyEnvironment(
  html: string,
  environment: SkyEnvironmentName | undefined,
): string {
  if (!isSkyEnvironment(environment)) return html;
  const tag = `<meta name="${SKY_ENVIRONMENT_META}" content="${escapeAttr(environment)}" />`;
  return html.replace("</head>", () => `    ${tag}\n  </head>`);
}

/** The environment the served document names, or undefined (no tag, or not a known value). */
export function readSkyEnvironment(doc: Document | undefined): SkyEnvironmentName | undefined {
  const content = doc
    ?.querySelector<HTMLMetaElement>(`meta[name="${SKY_ENVIRONMENT_META}"]`)
    ?.getAttribute("content")
    ?.trim();
  return isSkyEnvironment(content) ? content : undefined;
}
